/**
 * Groups similar skill slugs so one user can run separate provider profiles per line of work.
 */

export enum WorkProfileGroup {
  CLEANING_HOME = 'cleaning_home',
  CARE_HEALTH = 'care_health',
  TRADES_REPAIRS = 'trades_repairs',
  TRANSPORT_ERRANDS = 'transport_errands',
  OTHER = 'other',
}

export const WORK_PROFILE_GROUP_LABELS: Record<WorkProfileGroup, string> = {
  [WorkProfileGroup.CLEANING_HOME]: 'Home cleaning & chores',
  [WorkProfileGroup.CARE_HEALTH]: 'Care & wellness',
  [WorkProfileGroup.TRADES_REPAIRS]: 'Trades & repairs',
  [WorkProfileGroup.TRANSPORT_ERRANDS]: 'Driving & errands',
  [WorkProfileGroup.OTHER]: 'Other services',
};

/** Maps a catalog skill slug to the work profile group used for filtering chips. */
export function getWorkProfileGroupForSkillSlug(slug: string): WorkProfileGroup {
  return groupForSkillSlug(slug.trim());
}

function groupForSkillSlug(slug: string): WorkProfileGroup {
  const s = slug.toLowerCase();
  if (
    /physio|therapy|nurse|babysit|elderly|patient|medicine|massage|hospital|speech|occupational|newborn|toddler|bed_ridden|cardio_pulmonary|chirostretch|school_homework|orthopedic|neurological|pediatric|geriatric|post_surgery|spine|knee_hip|shoulder_neck/.test(
      s,
    )
  ) {
    return WorkProfileGroup.CARE_HEALTH;
  }
  if (/plumbing|electrical|carpentry|painting|false_ceiling|gardening|lawn_mowing|plant_care/.test(s)) {
    return WorkProfileGroup.TRADES_REPAIRS;
  }
  if (
    /driving|driver|delivery|errands|grocery|bill_payment|bank_documentation|government_form|instructor|outstation|bike_delivery/.test(
      s,
    )
  ) {
    return WorkProfileGroup.TRANSPORT_ERRANDS;
  }
  if (
    /cleaning|laundry|housekeeping|dish|bathroom|kitchen|window|ironing|curtain|tiffin|cooking|meal|dust|mop|sweep|utensil|fridge|microwave|wardrobe|shoe|waste|bed_making|deep_clean|balcony|ceiling_fan|silver_brass|dry_clean|juice_salad/.test(
      s,
    )
  ) {
    return WorkProfileGroup.CLEANING_HOME;
  }
  return WorkProfileGroup.OTHER;
}

/**
 * Picks the most common inferred group from selected skill slugs (ties favor first slug order).
 */
/** When `group` is `auto`, returns all slugs; otherwise only slugs belonging to that group. */
export function filterSkillSlugsByWorkProfileGroup(
  slugs: readonly string[],
  group: 'auto' | WorkProfileGroup,
): string[] {
  if (group === 'auto') return [...slugs];
  return slugs.filter((s) => groupForSkillSlug(s.trim()) === group);
}

export function inferWorkProfileGroupFromSkills(skills: string[]): WorkProfileGroup {
  if (skills.length === 0) return WorkProfileGroup.OTHER;
  const counts = new Map<WorkProfileGroup, number>();
  const order: WorkProfileGroup[] = [];
  for (const sk of skills) {
    const g = groupForSkillSlug(sk.trim());
    if (!counts.has(g)) order.push(g);
    counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  let best: WorkProfileGroup = WorkProfileGroup.OTHER;
  let max = 0;
  for (const g of order) {
    const c = counts.get(g) ?? 0;
    if (c > max) {
      max = c;
      best = g;
    }
  }
  return best;
}
